import { ChangeEvent, FC } from 'react';

import { MAX_CAMPAIGN_SELECT } from 'utils/constants/dashboard';

import dashboardMultiActionsButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/dashboard_multi_actions_button_click';
import Checkbox from '@hh.ru/bloko/build/blocks/checkbox';
import { HoverTip, Tip } from '@hh.ru/bloko/build/blocks/drop';
import { TipPlacement } from '@hh.ru/bloko/build/blocks/drop/Tip';
import { VSpacing } from '@hh.ru/magritte-ui';

import Icon, { IconColor, IconScale, IconView } from 'bloko/blocks/icon';
import Link, { LinkIconPosition } from 'bloko/blocks/link';

import useOnOffState from 'hooks/common/useOnOffState';
import useCheckedCampaignIdsForBudget from 'hooks/dashboard/useCheckedCampaignIdsForBudget';
import useTrls from 'hooks/translation/useTrls';

import BudgetIncrease from 'components/Dashboard/MultiActions/BudgetIncrease/BudgetIncrease';
import MultiStopConfirmationModal from 'components/Dashboard/MultiActions/MultiStopConfirmationModal/MultiStopConfirmationModal';

import { CampaignTabStatus, setCheckedCampaignIds } from 'store/models/dashboard';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/Dashboard/MultiActions/MultiActions.module.less';

const TrlKeys = {
    selectAll: 'Dashboard.MultiActions.selectAll',
    selectAllTooltip: 'Dashboard.MultiActions.selectAllTooltip',
    stop: 'Dashboard.MultiActions.stop',
    budgetIncrease: 'Dashboard.MultiActions.budgetIncrease',
};

const MultiActions: FC = () => {
    const campaignTabsStatus = useSelector((state) => state.campaignTabsStatus);
    const checkedCampaignIds = useSelector((state) => state.dashboard.checkedCampaignIds);
    const { availableCampaignIdsForBudget, stoppedByEndDateCampaignsCount, infinityBudgetCampaignsCount } =
        useCheckedCampaignIdsForBudget();

    const campaigns = useSelector((state) => state.dashboard.campaigns);
    const [isMultiStopModalVisible, setIsMultiStopModalVisibleOn, setIsMultiStopModalVisibleOff] = useOnOffState(false);

    const trl = useTrls();
    const dispatch = useDispatch();

    const checkedCampaignsCount = Object.keys(checkedCampaignIds).length;
    const campaignsForBudgetIncreaseCount = Object.keys(availableCampaignIdsForBudget).length;
    const campaignsCount = campaigns.length;

    const onStopClick = () => {
        setIsMultiStopModalVisibleOn();

        dashboardMultiActionsButtonClick({
            buttonName: 'dashboard_multi_actions_stop',
            checkedCampaignsCount,
            campaignIds: JSON.stringify(Object.keys(checkedCampaignIds).map(Number)),
        });
    };

    const onSelectAllToggle = (event: ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        const newCheckedCampaignIds: Record<number, boolean> = {};

        if (checked) {
            campaigns.slice(0, MAX_CAMPAIGN_SELECT).forEach((campaign) => {
                newCheckedCampaignIds[campaign.id] = true;
            });
        }
        dispatch(setCheckedCampaignIds(newCheckedCampaignIds));

        dashboardMultiActionsButtonClick({
            buttonName: 'dashboard_multi_actions_select_all',
            checkedCampaignsCount: Object.keys(newCheckedCampaignIds).length,
            campaignIds: JSON.stringify(Object.keys(newCheckedCampaignIds).map(Number)),
        });
    };

    if (campaignsCount === 0 || campaignTabsStatus === CampaignTabStatus.Off) {
        return null;
    }

    const selectAllIndeterminate =
        checkedCampaignsCount > 0 && checkedCampaignsCount < Math.min(campaignsCount, MAX_CAMPAIGN_SELECT);
    const selectAllChecked =
        checkedCampaignsCount > 0 && checkedCampaignsCount === Math.min(campaignsCount, MAX_CAMPAIGN_SELECT);

    return (
        <div className={styles.wrapper}>
            <VSpacing default={24} />
            <div className={styles.controls}>
                <div className={styles.checkbox}>
                    <Checkbox
                        checked={selectAllChecked}
                        indeterminate={selectAllIndeterminate}
                        onChange={onSelectAllToggle}
                        data-qa="multi-actions-select-all"
                    >
                        {trl(TrlKeys.selectAll)}
                    </Checkbox>
                    <HoverTip
                        placement={TipPlacement.Bottom}
                        render={() => trl(TrlKeys.selectAllTooltip)}
                        DropElement={Tip}
                    >
                        <Icon
                            highlighted={IconColor.Gray60}
                            initial={IconColor.Gray50}
                            view={IconView.Info}
                            scale={IconScale.Small}
                        />
                    </HoverTip>
                </div>
                {checkedCampaignsCount > 0 && (
                    <Link
                        onClick={onStopClick}
                        disabled={false}
                        icon={
                            <Icon
                                highlighted={IconColor.Blue70}
                                initial={IconColor.Blue50}
                                view={IconView.Pause}
                                scale={IconScale.Small}
                            />
                        }
                        iconPosition={LinkIconPosition.Left}
                        data-qa="campaign-multistop"
                    >
                        {trl(TrlKeys.stop, [checkedCampaignsCount])}
                    </Link>
                )}
                {campaignsForBudgetIncreaseCount > 0 && (
                    <BudgetIncrease
                        campaignsForBudgetIncreaseCount={campaignsForBudgetIncreaseCount}
                        infinityBudgetCampaignsCount={infinityBudgetCampaignsCount}
                        stoppedByEndDateCampaignsCount={stoppedByEndDateCampaignsCount}
                        availableCampaignIdsForBudget={availableCampaignIdsForBudget}
                    />
                )}
            </div>
            <VSpacing default={24} />
            {isMultiStopModalVisible && (
                <MultiStopConfirmationModal
                    isVisible={isMultiStopModalVisible}
                    hideModal={setIsMultiStopModalVisibleOff}
                />
            )}
        </div>
    );
};

export default MultiActions;
